import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Avatar,
  Box,
  Container,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import axios from "axios";
import { baseURL, config, requestID } from "../../../common/utils";
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
} from "@ionic/react";

function VotingPage() {
  const [positions, setPositions] = useState([]);
  const [electionName, setElectionName] = useState("");
  const [electionId, setElectionId] = useState("");
  const [loading, setLoading] = useState(true);
  const [votingCandidateId, setVotingCandidateId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    fetchElectionData();
  }, []);

  const fetchElectionData = async () => {
    try {
      setLoading(true);
      const payload = {
        requestID: requestID(),
        election_id: electionId,
      };
      const response = await axios.post(
        `${baseURL}elections/getAspirantsByPosition`,
        payload,
        config
      );
      console.log(response);
      if (!response.data.status) {
        throw new Error(response.data.message || "No active election found");
      }

      setElectionName(response.data.election_name);
      setElectionId(response.data.election_id);
      setPositions(response.data.positions);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to fetch election data",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmVote = (candidate) => {
    setSelectedCandidate(candidate);
    setConfirmOpen(true);
  };

  const handleVoteClick = async () => {
    if (!selectedCandidate) return;

    setConfirmOpen(false);
    setVotingCandidateId(selectedCandidate.user_id);
    console.log(selectedCandidate);

    try {
      const payload = {
        requestID: requestID(),
        candidate_id: selectedCandidate.user_id,
        position_id: selectedCandidate.position_id,
      };
      console.log(selectedCandidate.position_id);
      const response = await axios.post(
        `${baseURL}elections/castvote`,
        payload,
        config
      );
      fetchElectionData();
      console.log(response);
      if (response.data.status) {
        setSnackbar({
          open: true,
          message: response.data.message,
          severity: "success",
        });
      } else {
        setSnackbar({
          open: true,
          message: response.data.message,
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Error casting vote",
        severity: "error",
      });
    } finally {
      setVotingCandidateId(null);
      setSelectedCandidate(null);
    }
  };

  const handleCloseConfirm = () => {
    setConfirmOpen(false);
    setSelectedCandidate(null);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Voting Page</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        <Container
          maxWidth="lg"
          sx={{
            height: "100vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            p: isMobile ? 1 : 2,
          }}
        >
          {/* <Box sx={{ 
        py: 2,
        position: 'sticky',
        top: 0,
        backgroundColor: 'background.paper',
        zIndex: theme.zIndex.appBar,
        boxShadow: theme.shadows[1]
      }}>
        <Typography variant="h4" align="center" sx={{ 
          fontWeight: 'bold',
          color: 'primary.main',
          my: 6
        }}>
          {electionName}
        </Typography>
      </Box> */}

          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              py: 1,
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: theme.palette.grey[100],
              },
              "&::-webkit-scrollbar-thumb": {
                background: theme.palette.primary.main,
                borderRadius: "3px",
              },
            }}
          >
            {positions.length === 0 ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="50vh"
              >
                <Typography variant="h6" color="textSecondary">
                  No Active Election
                </Typography>
              </Box>
            ) : (
              positions.map((pos, index) => (
                <Card
                  key={index}
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    boxShadow: theme.shadows[2],
                    "&:hover": {
                      boxShadow: theme.shadows[4],
                    },
                  }}
                >
                  <CardContent>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: "bold",
                        color: "primary.main",
                        mb: 2,
                        pb: 1,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      {pos.position_name}
                    </Typography>
                    <Grid container spacing={2}>
                      {pos.aspirants.map((aspirant) => (
                        <Grid item xs={12} sm={6} md={4} key={aspirant.id}>
                          <Card
                            variant="outlined"
                            sx={{
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              transition: "transform 0.2s",
                              "&:hover": {
                                transform: "translateY(-4px)",
                              },
                            }}
                          >
                            <CardContent
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                flexGrow: 1,
                              }}
                            >
                              <Avatar
                                src={aspirant.photo_url || ""}
                                sx={{
                                  width: 80,
                                  height: 80,
                                  mb: 2,
                                  fontSize: "2.5rem",
                                  backgroundColor: theme.palette.primary.light,
                                }}
                              />
                              <Typography
                                variant="h6"
                                align="center"
                                gutterBottom
                                sx={{ fontWeight: 600 }}
                              >
                                {aspirant.full_name}
                              </Typography>
                              {aspirant.party && (
                                <Typography
                                  variant="subtitle2"
                                  color="textSecondary"
                                  gutterBottom
                                >
                                  {aspirant.user_id}
                                </Typography>
                              )}
                              <Box sx={{ mt: "auto", width: "100%" }}>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  onClick={() => handleConfirmVote(aspirant)}
                                  sx={{ mt: 2 }}
                                  disabled={pos.voteStatus === "true"}
                                  fullWidth
                                  size={isMobile ? "small" : "medium"}
                                >
                                  {votingCandidateId === aspirant.id ? (
                                    <CircularProgress
                                      size={24}
                                      color="inherit"
                                    />
                                  ) : (
                                    "Vote"
                                  )}
                                </Button>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              ))
            )}
          </Box>

          <Dialog
            open={confirmOpen}
            onClose={handleCloseConfirm}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">Confirm Your Vote</DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                Are you sure you want to vote for {selectedCandidate?.full_name}
                ?
                <br />
                <strong>This action cannot be undone.</strong>
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseConfirm} color="primary">
                Cancel
              </Button>
              <Button onClick={handleVoteClick} color="primary" autoFocus>
                Confirm Vote
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert
              severity={snackbar.severity}
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              sx={{ width: "100%" }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Container>
      </IonContent>
    </IonPage>
  );
}

export default VotingPage;
